import React, { createRef, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import Button from "../common/Button";
import TextBox from "../common/TextBox";
import ComboBox from "../common/ComboBox";
import { langComponent } from "../../constants";
import AppContext from "../../AppContext";

import { ReactComponent as ArrowLeftSvg } from "../../assets/icons/arrow-left.svg";
import { ReactComponent as ArrowRightDisabledSvg } from "../../assets/icons/arrow-right-disabled.svg";
import { ReactComponent as ArrowRightSvg } from "../../assets/icons/arrow-right.svg";
import OnboardContext from "./context";
import FileUploader from "../common/FileUploader";
import { format } from "date-fns";
import _ from "lodash";
import useCertificates from "../../hooks/useCertificates";
import { ReactComponent as InfoSvg } from "../../assets/icons/info.svg";
import { Tooltip } from "react-tooltip";

const monthOptions = _.range(1, 13).map((_, month) => ({
  name: format(new Date(1900, month, 1), "MMMM"),
  value: (month + 1).toString(),
}));

const Certificate = (props) => {
  const { goBack, goForward, information, addInformation } =
    useContext(OnboardContext);
  const [file, setFile] = useState(information.certificate?.file || undefined);
  const { isAuth, user, getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.onboard);
  const TranslationCommon = getLang(langComponent.common);
  const TranslationCertificate = getLang(langComponent.certificate);
  const { queryCertificate } = useCertificates();
  const [existingCertificates, setExistingCertificates] = useState([]);

  const handleSkip = () => {
    addInformation({ certificate: undefined });
    goForward();
  };

  useEffect(() => {
    // * Get current certificates to display
    if (isAuth && user) {
      queryCertificate(
        {
          createdBy: user._id,
          company: null,
          active: true,
          deleted: false,
        },
        null,
        -1,
        {
          _id: 1,
        }
      ).then(({ data }) => {
        setExistingCertificates(data);
      });
    }
  }, []);

  const handleFileChange = (files) => {
    if (files && files.length > 0) {
      setFile(files[0]);
    }
  };

  const handleSubmit = ({ name, issuer, month, year, url }) => {
    addInformation({
      certificate: { file: file, name, issuer, month, year, url },
    });
    goForward();
  };

  const handleValidate = ({ name, issuer, month, year }) => {
    let errors = {};

    if (!name) errors.name = TranslationCertificate("Name cannot be blank");
    if (!issuer)
      errors.issuer = TranslationCertificate(
        "Organization Issuer cannot be blank"
      );
    if (!month) errors.month = TranslationCertificate("Please specify a month");
    if (!year || year > 9999 || year < 0)
      errors.year = TranslationCertificate("Please specify a year");

    return errors;
  };

  return (
    <div className="onboard-info">
      <h1 className="onboard__header">{Translation("Have a certificate?")}</h1>
      <Formik
        initialValues={
          information?.certificate || {
            name: "",
            issuer: "",
            month: "",
            year: "",
            url: "",
          }
        }
        validate={handleValidate}
        onSubmit={handleSubmit}
      >
        {({ isValid }) => {
          return (
            <Form className="onboard__form onboard-certificate">
              <div className="onboard__form-item">
                <TextBox
                  name="name"
                  placeholder={TranslationCertificate(
                    "Name of certification Ex. Urbann Certification"
                  )}
                  style={{ gridColumn: "1 / 3" }}
                  label="Certificate name"
                />
              </div>
              <div className="onboard__form-item">
                <TextBox
                  name="issuer"
                  placeholder={TranslationCertificate(
                    "Issuing organization Ex. Urbann"
                  )}
                  style={{ gridColumn: "1 / 3" }}
                  label="Issuer"
                />
              </div>
              <div className="onboard__form-item">
                <div className="onboard-certificate__split">
                  <ComboBox
                    placeholder={TranslationCommon("Select month")}
                    name="month"
                    options={monthOptions}
                    label="Month"
                  />
                  <TextBox
                    name="year"
                    placeholder={TranslationCommon("Year")}
                    type="number"
                    label="Year"
                  />
                </div>
              </div>
              <div className="onboard__form-item">
                <TextBox
                  name="url"
                  placeholder={TranslationCertificate(
                    "Certification URL (optional)"
                  )}
                  type="text"
                  style={{ gridColumn: "1 / 3" }}
                  label="Certification URL (optional)"
                />
              </div>
              <div className="onboard__form-item">
                <FileUploader
                  label={
                    !file
                      ? TranslationCertificate("Upload certificate")
                      : TranslationCertificate("Change certificate")
                  }
                  onChange={handleFileChange}
                />
                <div className="onboard-certificate__file">
                  {file && `${file.name} - ${file.type}`}
                </div>
              </div>
              {/* {existingCertificates.length > 0 && (
                <div className="onboard-certificate-existing">
                  <span className="onboard-certificate-existing__title">
                    Existing certificates will be transferred over
                  </span>
                  <div className="onboard-certificate-existing-list">
                    {existingCertificates.map((cert) => {
                      return (
                        <div className="onboard-certificate-existing-list-item">
                          <span className="onboard-certificate-existing-list-item__name">
                            {cert.name}
                          </span>
                          <span className="onboard-certificate-existing-list-item__date">
                            {format(new Date(cert.effectiveDate), "MMM yyyy")}
                          </span>
                          <span className="onboard-certificate-existing-list-item__issuer">
                            Issued by {cert.issuer}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )} */}
              <div className="onboard__form-cta">
                <Button
                  label={TranslationCommon("Return")}
                  leadingIcon={<ArrowLeftSvg />}
                  onClick={goBack}
                  variant="secondary"
                />
                <div className="onboard__form-cta__divider" />
                <Button
                  label={
                    existingCertificates.length > 0
                      ? TranslationCommon("Skip adding a new one")
                      : TranslationCommon("Skip")
                  }
                  variant="tertiary"
                  onClick={handleSkip}
                />
                <Button
                  type="submit"
                  label={TranslationCommon("Continue")}
                  trailingIcon={
                    isValid ? <ArrowRightSvg /> : <ArrowRightDisabledSvg />
                  }
                  disabled={!isValid}
                />
              </div>
              {existingCertificates.length > 0 && (
                <span className="onboard-certificate-existing">
                  <InfoSvg
                    height={16}
                    data-tooltip-id="tag-tooltip"
                    data-tooltip-content={`All ${existingCertificates.length} certificates will be transfered over to the company`}
                  />
                  Existing certificates will be transferred over
                </span>
              )}
            </Form>
          );
        }}
      </Formik>
      <Tooltip id="tag-tooltip" />
    </div>
  );
};

Certificate.propTypes = {};

export default Certificate;
